
import React, { useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { useImportBasketMutation } from "../../../api/endpoints/basket";
import { useLanguage } from "../../../contexts/LanguageContext";
import { toastService } from "../../../utils/toastService";

const ImportBasketDialog = ({ visible, onHide, onImported, t }) => {
  const { th } = useLanguage();
  const toast = useRef(null);
  const [file, setFile] = useState(null);
  const [importBasket, { isLoading }] = useImportBasketMutation();

  const onFileChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
  };

  const handleImport = async () => {
    if (!file) {
      toast.current?.show({
        severity: "warn",
        summary: th("common.warning"),
        detail: th("basket.selectFileFirst"),
        life: 2500,
      });
      return;
    }
    try {
      const resp = await importBasket(file).unwrap();
      const erros = resp?.data?.erros ?? resp?.data?.Erros ?? null;
      if (erros && typeof erros === "object" && Object.keys(erros).length) {
  Object.entries(erros).forEach(([partCode, reason]) => {
    if (reason) toastService.warn(`${partCode}: ${String(reason)}`);
  });
}

      toast.current?.show({
        severity: "success",
        summary: th("common.success"),
        detail: th("basket.importSuccess"),
        life: 2500,
      });

      setFile(null);
      onImported?.();
      onHide?.();
    } catch (e) {
      const msg = e?.data?.errorMessage || th("basket.importError");
      toast.current?.show({
        severity: "error",
        summary: t("common.error"),
        detail: msg,
        life: 3500,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={th("common.cancel")}
        icon="pi pi-times"
        className="p-button-outlined"
        onClick={() => {
          setFile(null);
          onHide?.();
        }}
        disabled={isLoading}
      />
      <Button
        label={th("common.import")}
        icon="pi pi-upload"
        onClick={handleImport}
        loading={isLoading}
      />
    </div>
  );

  return (
    <Dialog
      header={th("basket.importFromExcel")}
      visible={visible}
      onHide={onHide}
      style={{ width: "36rem" }}
      modal
      footer={footer}
    >
      <Toast ref={toast} />

      {isLoading && (
        <div className="flex align-items-center gap-2 mb-3 p-2 border-1 surface-border border-round">
          <span>{th("basket.importing")}</span>
          <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="6" />
        </div>
      )}

      <div className="flex flex-column gap-3">
        <div className="field">
          <label className="block mb-2">{th("basket.selectExcelFile")}</label>
          <input
            type="file"
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            onChange={onFileChange}
            disabled={isLoading}
          />
          {file && (
            <small className="block mt-2">
              {th("basket.selectedFile")}: {file.name}
            </small>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default ImportBasketDialog;
